// client/src/components/cursos/CursosPage.js
import React, { Fragment, useState, useMemo, useCallback } from 'react';
import { useCursos, useMutateCursos } from '../../hooks/useCursos';
import { useMyAccountDetails } from '../../hooks/useAccounts';
import CursoForm from './CursoForm';
import CursoRow from './CursoRow';
import FiltroCursos from './FiltroCursos';
import MatriculasPage from './MatriculasPage';
import { GraduationCap, PlusCircle, Filter as FilterIcon, Loader2, XCircle, SquarePen, Trash2, Users } from 'lucide-react';
import Pagination from '../common/Pagination';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';

const CursosPage = () => {
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilters, setActiveFilters] = useState({});
    const [currentCurso, setCurrentCurso] = useState(null);
    const [selectedCurso, setSelectedCurso] = useState(null);
    const [cursoMatriculas, setCursoMatriculas] = useState(null);
    const cursosPerPage = 20;

    const { data: accountData } = useMyAccountDetails();
    const accountId = accountData?._id;

    const queryParams = useMemo(() => ({
        page: currentPage,
        limit: cursosPerPage,
        ...activeFilters
    }), [currentPage, activeFilters]);

    const { data: cursosData, isLoading, refetch } = useCursos(accountId, queryParams);
    const { deleteCurso, isPending } = useMutateCursos();

    const cursos = cursosData?.data || [];
    const totalCursos = cursosData?.totalCount || cursosData?.count || 0;

    const handleApplyFilters = useCallback((filters) => {
        setCurrentPage(1);
        setActiveFilters(filters);
    }, []);

    const handleAdicionarClick = useCallback(() => {
        setCurrentCurso(null);
        setIsFormVisible(true);
    }, []);

    const handleCloseForm = useCallback(() => {
        setIsFormVisible(false);
        setCurrentCurso(null);
    }, []);

    const handleDelete = useCallback((cursoId) => {
        if (window.confirm('Tem certeza que deseja excluir este curso?')) {
            deleteCurso.mutate(cursoId, {
                onSuccess: () => setSelectedCurso(null),
            });
        }
    }, [deleteCurso]);

    const handleSaveSuccess = useCallback(() => {
        handleCloseForm();
        refetch();
    }, [handleCloseForm, refetch]);

    const handleEditClick = useCallback((curso) => {
        setCurrentCurso(curso);
        setIsFormVisible(true);
        setSelectedCurso(null);
    }, []);

    if (cursoMatriculas) {
        return <MatriculasPage curso={cursoMatriculas} onClose={() => setCursoMatriculas(null)} />;
    }

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<GraduationCap />}
                    title="Cursos"
                    subtitle={!isFormVisible ? `${totalCursos} curso(s)` : null}
                    actions={<>
                        {!isFormVisible && (
                            <>
                                <button
                                    onClick={() => setShowFilters(p => !p)}
                                    className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                                    disabled={isLoading}
                                >
                                    <FilterIcon size={18} className="icon" />
                                    {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                                </button>
                            </>
                        )}
                        {isFormVisible && (
                            <button onClick={handleCloseForm} className="action-button action-button-secondary" disabled={isLoading}>
                                <XCircle size={18} className="icon" /> Fechar
                            </button>
                        )}
                    </>}
                />

                {isFormVisible && (
                    <div className="content-panel">
                        <CursoForm
                            key={currentCurso?._id || 'new'}
                            onCloseForm={handleCloseForm}
                            onSaveSuccess={handleSaveSuccess}
                            current={currentCurso}
                        />
                    </div>
                )}

                {showFilters && !isFormVisible && (
                    <div className="content-panel">
                        <h3 className="panel-title">Filtros</h3>
                        <FiltroCursos onApplyFilters={handleApplyFilters} loading={isLoading} />
                    </div>
                )}
            </div>

            {!isFormVisible && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin" />
                                <span>Carregando cursos...</span>
                            </div>
                        ) : cursos.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Categoria</th>
                                        <th>Valor</th>
                                        <th>Carga Horária</th>
                                        <th>Status</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cursos.map(curso => (
                                        <CursoRow
                                            key={curso.id}
                                            curso={curso}
                                            onOpenActions={setSelectedCurso}
                                            loading={isPending}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <GraduationCap size={48} />
                                <h3>Nenhum curso encontrado</h3>
                                <p>
                                    {Object.keys(activeFilters).length > 0
                                        ? 'Nenhum curso encontrado com os filtros aplicados.'
                                        : 'Clique em "Adicionar Curso" para cadastrar o primeiro.'}
                                </p>
                            </div>
                        )}
                    </div>

                    {totalCursos > cursosPerPage && (
                        <Pagination
                            itemsPerPage={cursosPerPage}
                            totalItems={totalCursos}
                            onPageChange={setCurrentPage}
                            currentPage={currentPage}
                        />
                    )}
                </div>
            )}

            <FabButton
                onClick={handleAdicionarClick}
                title="Adicionar Curso"
                visible={!isFormVisible}
                disabled={isLoading}
                icon={<GraduationCap size={26} className="icon" />}
            />

            <ActionsDrawer
                open={!!selectedCurso}
                onClose={() => setSelectedCurso(null)}
                title={selectedCurso?.nome || 'Curso'}
                actions={selectedCurso ? [
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <SquarePen size={20} className="icon" />,
                        onClick: () => handleEditClick(selectedCurso),
                    },
                    {
                        key: 'matriculados',
                        label: 'Ver Matriculados',
                        icon: <Users size={20} className="icon" />,
                        onClick: () => { setCursoMatriculas(selectedCurso); setSelectedCurso(null); },
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedCurso.id),
                        destructive: true,
                        loading: isPending,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default CursosPage;
